import { useMemo } from 'react';
import { useQuery } from '@ts-core/lib/apollo';

import { StudentActivity } from '@ts-core/modules/activities/types';
import { getActivitiesQuery, GetActivitiesQueryResult, GetActivitiesQueryVariables } from '../graphql/queries/getActivitiesQuery';

export interface ActivitiesHookInput {
  courseId: string;
}

export interface ActivitiesHookPayload {
  allActivities?: StudentActivity[];
  activeActivities?: StudentActivity[];
  expiredActivities?: StudentActivity[];
  pendingActivities?: StudentActivity[];
  loading: boolean;
}

export default function useActivities({ courseId }: ActivitiesHookInput): ActivitiesHookPayload {
  const { data, loading } = useQuery<GetActivitiesQueryResult, GetActivitiesQueryVariables>(getActivitiesQuery, {
    variables: { courseId },
    fetchPolicy: 'cache-and-network',
  });

  const activities = data?.activities;
  const expiredActivities = useMemo(() => {
    return activities?.filter((activity) => activity.expired);
  }, [activities]);

  const activeActivities = useMemo(() => {
    return activities
      ?.filter((activity) => !activity.expired)
      .sort((a, b) => {
        if (a.answered === b.answered) {
          return 0;
        }
        if (a.answered) {
          return 1;
        }
        return -1;
      });
  }, [activities]);

  const pendingActivities = useMemo(() => {
    return activities?.filter((activity) => !activity.answered && !activity.expired);
  }, [activities]);

  return {
    allActivities: activities,
    activeActivities,
    expiredActivities,
    loading,
    pendingActivities,
  };
}
